import React from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Home,
  ArrowLeft,
  Search,
  AlertCircle,
  Server,
  Shield,
  WifiOff,
  FileQuestion,
  RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import SEOHead from '@/components/seo/SEOHead';
import useAuthStore from '@/store/authStore';
import { ThemeProvider } from '@/components/ThemeProvider';

// Configuration des différents types d'erreurs
const errorConfig = {
  404: {
    title: 'Page introuvable',
    message: 'La page que vous recherchez n\'existe pas ou a été déplacée.',
    icon: FileQuestion,
    color: 'text-primary',
    bgGradient: 'from-primary/20 to-primary/5',
    suggestions: [
      'Vérifiez l\'URL dans la barre d\'adresse',
      'Utilisez le menu de navigation pour trouver ce que vous cherchez',
      'Retournez à la page d\'accueil'
    ]
  },
  500: {
    title: 'Erreur serveur',
    message: 'Une erreur interne s\'est produite. Notre équipe a été notifiée.',
    icon: Server,
    color: 'text-destructive',
    bgGradient: 'from-destructive/20 to-destructive/5',
    suggestions: [
      'Réessayez dans quelques instants',
      'Vérifiez votre connexion internet',
      'Contactez le support si le problème persiste'
    ]
  },
  403: {
    title: 'Accès refusé',
    message: 'Vous n\'avez pas les permissions nécessaires pour accéder à cette page.',
    icon: Shield,
    color: 'text-secondary',
    bgGradient: 'from-secondary/20 to-secondary/5',
    suggestions: [
      'Vérifiez que vous êtes connecté avec le bon compte',
      'Contactez un administrateur si vous pensez que c\'est une erreur',
      'Retournez à votre tableau de bord'
    ]
  },
  network: {
    title: 'Problème de connexion',
    message: 'Impossible de se connecter au serveur. Vérifiez votre connexion internet.',
    icon: WifiOff,
    color: 'text-muted-foreground',
    bgGradient: 'from-muted/20 to-muted/5',
    suggestions: [
      'Vérifiez votre connexion internet',
      'Réessayez dans quelques instants',
      'Contactez le support si le problème persiste'
    ]
  },
  default: {
    title: 'Une erreur est survenue',
    message: 'Quelque chose s\'est mal passé. Veuillez réessayer.',
    icon: AlertCircle,
    color: 'text-muted-foreground',
    bgGradient: 'from-muted/20 to-muted/5',
    suggestions: [
      'Actualisez la page',
      'Retournez à la page précédente',
      'Contactez le support si le problème persiste'
    ]
  }
};

const ErrorPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { isAuthenticated, user } = useAuthStore();

  // Déterminer le type d'erreur depuis l'état de la location, l'URL ou par défaut
  const getErrorType = () => {
    // Vérifier d'abord les paramètres d'URL
    if (params.code) {
      const code = parseInt(params.code);
      if (!isNaN(code) && errorConfig[code]) {
        return code;
      }
      // Si c'est un type d'erreur textuel (network, etc.)
      if (errorConfig[params.code]) {
        return params.code;
      }
    }

    // Vérifier l'état de la location
    const state = location.state;
    if (state?.errorCode) {
      return state.errorCode;
    }
    if (state?.errorType) {
      return state.errorType;
    }

    // Par défaut, 404 pour les routes non trouvées
    return 404;
  };

  const errorType = getErrorType();
  const config = errorConfig[errorType] || errorConfig.default;
  const Icon = config.icon;

  // Déterminer où rediriger selon l'authentification
  const getHomePath = () => {
    if (!isAuthenticated) return '/';
    if (user?.role === 'CLIENT') return '/client/dashboard';
    if (user?.role === 'TRANSPORTEUR') return '/transporter/dashboard';
    if (user?.role === 'ADMIN' || user?.role === 'AGENT') return '/admin/dashboard';
    return '/';
  };

  const handleGoHome = () => {
    navigate(getHomePath());
  };

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      handleGoHome();
    }
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  // Animations
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut'
      }
    }
  };

  const iconVariants = {
    hidden: { scale: 0, rotate: -180 },
    visible: {
      scale: 1,
      rotate: 0,
      transition: {
        type: 'spring',
        stiffness: 200,
        damping: 15,
        delay: 0.3
      }
    },
    hover: {
      scale: 1.1,
      rotate: [0, -10, 10, -10, 0],
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
      <SEOHead
        title={`${config.title} - LogiConnect`}
        description={config.message}
        noindex={true}
      />
      <div className="min-h-screen bg-background flex items-center justify-center p-4 relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className={`absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-br ${config.bgGradient} rounded-full blur-3xl opacity-30 animate-blob`} />
          <div className={`absolute bottom-1/4 right-1/4 w-96 h-96 bg-gradient-to-br ${config.bgGradient} rounded-full blur-3xl opacity-30 animate-blob animation-delay-2000`} />
          <div className={`absolute top-1/2 right-1/3 w-96 h-96 bg-gradient-to-br ${config.bgGradient} rounded-full blur-3xl opacity-30 animate-blob animation-delay-4000`} />
        </div>

        {/* Grid pattern overlay */}
        <div
          className="absolute inset-0 pointer-events-none opacity-30 dark:opacity-20"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '20px 20px'
          }}
        />

        <motion.div
          className="relative z-10 w-full max-w-2xl"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Card className="backdrop-blur-sm bg-card/80 border-border/50 shadow-2xl">
            <CardContent className="p-8 md:p-12">
              {/* Icon */}
              <motion.div
                className="flex justify-center mb-6"
                variants={itemVariants}
              >
                <motion.div
                  className={`relative ${config.color}`}
                  variants={iconVariants}
                  whileHover="hover"
                >
                  <div className={`absolute inset-0 ${config.color} opacity-20 blur-2xl`} />
                  <Icon className="relative w-24 h-24 md:w-32 md:h-32" strokeWidth={1.5} />
                </motion.div>
              </motion.div>

              {/* Error Code */}
              <motion.div
                className="text-center mb-4"
                variants={itemVariants}
              >
                <motion.h1
                  className="text-8xl md:text-9xl font-display font-bold bg-gradient-to-br from-foreground to-foreground/50 bg-clip-text text-transparent"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{
                    type: 'spring',
                    stiffness: 200,
                    damping: 15,
                    delay: 0.5
                  }}
                >
                  {errorType === 'network' ? '!' : errorType}
                </motion.h1>
              </motion.div>

              {/* Title */}
              <motion.h2
                className="text-3xl md:text-4xl font-display font-bold text-center mb-4"
                variants={itemVariants}
              >
                {config.title}
              </motion.h2>

              {/* Message */}
              <motion.p
                className="text-muted-foreground text-center text-lg mb-8 max-w-md mx-auto"
                variants={itemVariants}
              >
                {config.message}
              </motion.p>

              {/* Suggestions */}
              <motion.div
                className="mb-8"
                variants={itemVariants}
              >
                <div className="space-y-3">
                  {config.suggestions.map((suggestion, index) => (
                    <motion.div
                      key={index}
                      className="flex items-start gap-3 text-sm text-muted-foreground"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.8 + index * 0.1 }}
                    >
                      <div className="mt-1 w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                      <span>{suggestion}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Action Buttons */}
              <motion.div
                className="flex flex-col sm:flex-row gap-4 justify-center items-center"
                variants={itemVariants}
              >
                <Button
                  onClick={handleGoHome}
                  size="lg"
                  className="w-full sm:w-auto min-w-[160px]"
                >
                  <Home className="mr-2 h-4 w-4" />
                  {isAuthenticated ? 'Tableau de bord' : 'Page d\'accueil'}
                </Button>
                <Button
                  onClick={handleGoBack}
                  variant="outline"
                  size="lg"
                  className="w-full sm:w-auto min-w-[160px]"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Retour
                </Button>
                {errorType === 500 || errorType === 'network' ? (
                  <Button
                    onClick={handleRefresh}
                    variant="ghost"
                    size="lg"
                    className="w-full sm:w-auto min-w-[160px]"
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Actualiser
                  </Button>
                ) : null}
              </motion.div>

              {/* Search suggestion for 404 */}
              {errorType === 404 && (
                <motion.div
                  className="mt-8 pt-8 border-t border-border"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.2 }}
                >
                  <p className="text-sm text-muted-foreground text-center mb-4">
                    Vous cherchez quelque chose de spécifique ?
                  </p>
                  <Button
                    onClick={() => navigate('/')}
                    variant="ghost"
                    size="sm"
                    className="w-full"
                  >
                    <Search className="mr-2 h-4 w-4" />
                    Explorer la plateforme
                  </Button>
                </motion.div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </ThemeProvider>
  );
};

export default ErrorPage;
